import React, { useState, useEffect } from "react";
import TopBanner from "../../components/TopBanner/TopBanner";
import Loading from "../../components/Loading/Loading";
import Header from "../../components/Header/Header";
import Footer from "../../components/Footer/Footer";
import {
  CANCELLED_BY_ADMIN,
  DELETED,
  ERROR,
  RED,
  SESSION_EXPIRED,
  SOMETHING_WENT_WRONG,
  priceRound,
  showSnackbar,
} from "../../../utils";
import { getImageUrl, invoiceURL, pnrURL } from "../../api/util";
import { useNavigate, Link } from "react-router-dom";
import { bookingDetail, cancelBooking, updateBooking } from "../../api/booking";
import RoutesList from "../../constants/RoutesList";
import CountriesList from "../../constants/Countrues";
import AirlinesRoundedIcon from "@mui/icons-material/AirlinesRounded";
import LocalPrintshopRoundedIcon from "@mui/icons-material/LocalPrintshopRounded";
import AirlineSeatReclineNormalSharpIcon from "@mui/icons-material/AirlineSeatReclineNormalSharp";
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";

const BookingDetails = () => {
  const navigate = useNavigate();

  const bookingId = new URL(document.location).searchParams.get("booking_id");

  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState({});
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isMutating, setIsMutating] = useState(false);
  const [editPassengers, setEditPassengers] = useState([]);
  const [showEditErrors, setShowEditErrors] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  const isCancelled = data?.status === DELETED || data?.status === "Cancelled";

  const countPassengerOnType = (paxType) => {
    return data?.passengers?.filter((pax) => pax?.pax_type === paxType)?.length;
  };

  const pricePassengerOnType = (paxType) => {
    return data?.passengers?.find((pax) => pax?.pax_type === paxType)
      ?.ticket_price;
  };

  const fetchBookingDetails = async () => {
    setIsLoading(true);
    try {
      const result = await bookingDetail(bookingId);
      setData(result);
    } catch (err) {
      if (err === "sessionTimeout") {
        showSnackbar(SESSION_EXPIRED, ERROR);
        localStorage.removeItem("Token");
        navigate(RoutesList.login);
      } else {
        showSnackbar(SOMETHING_WENT_WRONG, ERROR);
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (Array.isArray(data?.passengers)) {
      setEditPassengers(
        data.passengers.map((passenger) => ({
          title: passenger?.title || "",
          first_name: passenger?.first_name || "",
          last_name: passenger?.last_name || "",
          contact_number: passenger?.contact_number || "",
          email: passenger?.email || "",
          passport_number: passenger?.passport_number || "",
          dob: passenger?.dob || "",
          passport_issued_at: passenger?.passport_issued_at || "",
          passport_expiring_at: passenger?.passport_expiring_at || "",
          passport_issue_country: passenger?.passport_issue_country || "",
          nationality: passenger?.nationality || "",
          gender: passenger?.gender || "",
          wheel_chair_required: passenger?.wheel_chair_required || false,
          veg_meal: passenger?.veg_meal || false,
        })),
      );
    }
  }, [data]);

  const openEdit = () => {
    setIsEditOpen(true);
  };

  const closeEdit = () => {
    setIsEditOpen(false);
  };

  const updatePassengerField = (index, field, value) => {
    setEditPassengers((prev) =>
      prev.map((passenger, i) =>
        i === index ? { ...passenger, [field]: value } : passenger,
      ),
    );
  };

  const toDateOnly = (value) => {
    if (!value) return null;
    const date = new Date(value);
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  };

  const getAgeInYears = (dobValue) => {
    const dob = toDateOnly(dobValue);
    if (!dob) return null;
    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const monthDiff = today.getMonth() - dob.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
      age -= 1;
    }
    return age;
  };

  const isValidEmail = (value) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value || "");
  };

  const isValidContact = (value) => {
    return /^\d{7,15}$/.test(value || "");
  };

  const isValidPassportNumber = (value) => {
    return /^[A-Za-z0-9]{6,15}$/.test(value || "");
  };

  const validatePassenger = (passenger) => {
    const dob = toDateOnly(passenger?.dob);
    const passportIssue = toDateOnly(passenger?.passport_issued_at);
    const passportExpiry = toDateOnly(passenger?.passport_expiring_at);
    const age = getAgeInYears(passenger?.dob);
    const isAdult = passenger?.pax_type === "Adult";

    return (
      passenger?.title &&
      passenger?.first_name?.trim().length > 0 &&
      passenger?.last_name?.trim().length > 0 &&
      isValidContact(passenger?.contact_number) &&
      isValidEmail(passenger?.email) &&
      isValidPassportNumber(passenger?.passport_number) &&
      dob != null &&
      passportIssue != null &&
      passportExpiry != null &&
      passportIssue > dob &&
      passportExpiry > passportIssue &&
      (isAdult ? age >= 12 : true) &&
      passenger?.passport_issue_country &&
      passenger?.nationality &&
      passenger?.gender
    );
  };

  const handleCancelBooking = async () => {
    setIsMutating(true);
    try {
      const result = await cancelBooking(bookingId);
      if (result?.status === "ok") {
        showSnackbar("Booking cancelled successfully");
        await fetchBookingDetails();
      } else {
        showSnackbar(result?.errors || "Unable to cancel booking", ERROR);
      }
    } catch (err) {
      if (err === "sessionTimeout") {
        showSnackbar(SESSION_EXPIRED, ERROR);
        localStorage.removeItem("Token");
        navigate(RoutesList.login);
      } else {
        showSnackbar(SOMETHING_WENT_WRONG, ERROR);
      }
    } finally {
      setIsMutating(false);
    }
  };

  const handleSaveBooking = async () => {
    const allValid = editPassengers.every((passenger) =>
      validatePassenger(passenger),
    );
    if (!allValid) {
      setShowEditErrors(true);
      showSnackbar("Please fill all required fields correctly", ERROR);
      return;
    }
    setIsMutating(true);
    try {
      const passengers = {};
      editPassengers.forEach((passenger, index) => {
        passengers[index] = passenger;
      });
      const result = await updateBooking(bookingId, { passengers });
      if (result?.status === "ok") {
        showSnackbar("Booking updated successfully");
        closeEdit();
        await fetchBookingDetails();
      } else {
        showSnackbar(result?.errors || "Unable to update booking", ERROR);
      }
    } catch (err) {
      if (err === "sessionTimeout") {
        showSnackbar(SESSION_EXPIRED, ERROR);
        localStorage.removeItem("Token");
        navigate(RoutesList.login);
      } else {
        showSnackbar(SOMETHING_WENT_WRONG, ERROR);
      }
    } finally {
      setIsMutating(false);
    }
  };

  const passengerFieldError = (passenger, key) => {
    if (!showEditErrors) return false;
    const value = passenger?.[key];
    switch (key) {
      case "title":
      case "passport_issue_country":
      case "nationality":
      case "gender":
        return !value;
      case "first_name":
      case "last_name":
      case "contact_number":
      case "email":
      case "passport_number":
        return !value || String(value).trim().length === 0;
      case "dob":
        return (
          !value ||
          (passenger?.pax_type === "Adult" && getAgeInYears(value) < 12)
        );
      case "passport_issued_at":
        return !value || toDateOnly(value) <= toDateOnly(passenger?.dob);
      case "passport_expiring_at":
        return (
          !value ||
          toDateOnly(value) <= toDateOnly(passenger?.passport_issued_at)
        );
      default:
        return false;
    }
  };

  useEffect(() => {
    fetchBookingDetails();
  }, []);

  return isLoading ? (
    <Loading />
  ) : (
    <>
      <Header />
      <TopBanner pagName={"Booking Details"} />
      <section
        id="tour_booking_submission"
        className="section_padding"
        style={{ backgroundColor: "#faf7f0", minHeight: "100vh" }}
      >
        <div className="container">
          <div className="row">
            <div className="col-lg-8">
              <div className="tou_booking_form_Wrapper">
                <div className="booking_tour_form">
                  <h3 className="heading_theme">Flight Information</h3>
                  {/* <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      gap: 12,
                      marginBottom: 14,
                      flexWrap: "wrap",
                    }}
                  >
                    <Button
                      variant="outlined"
                      onClick={openEdit}
                      disabled={isCancelled || isMutating}
                      style={{
                        borderColor: "#c8a24a",
                        color: "#2b2540",
                        fontWeight: 600,
                        background: "#fff",
                        minWidth: 132,
                      }}
                    >
                      Edit Booking
                    </Button>
                    <Button
                      variant="contained"
                      onClick={() => setShowCancelConfirm(true)}
                      disabled={isCancelled || isMutating}
                      style={{
                        background: isCancelled
                          ? "#b7b7b7"
                          : "linear-gradient(180deg, #3a3a3a 0%, #222222 100%)",
                        color: "#fff",
                        fontWeight: 600,
                        minWidth: 152,
                      }}
                    >
                      {isCancelled ? "Cancelled" : "Cancel Booking"}
                    </Button>
                  </div> */}
                  <div
                    className="flight_search_items tour_booking_form_box"
                    style={{
                      justifyContent: "center",
                      borderWidth: 0,
                    }}
                  >
                    <div className="multi_city_flight_lists">
                      <div className="flight_multis_area_wrapper">
                        <div>
                          <div
                            className="booking_success_img"
                            style={{ width: 50, height: 50 }}
                          >
                            {data?.carrier?.logo != null ? (
                              <img
                                src={getImageUrl(data?.carrier?.logo)}
                                alt="img"
                              />
                            ) : (
                              <AirlinesRoundedIcon
                                fontSize="large"
                                color="info"
                                style={{ alignSelf: "center" }}
                              />
                            )}
                          </div>
                          <h3 style={{ fontWeight: "500", fontSize: 14 }}>
                            {data?.carrier?.name}
                          </h3>
                          <h3 style={{ fontWeight: "500" }}>
                            {data?.onward_trip_flight_number}
                          </h3>
                        </div>
                        <div className="flight_search_left">
                          <div className="flight_logo">
                            <img
                              src={require("../../assets/img/icon/plan-md.png")}
                              width={75}
                              height={75}
                              style={{ resizeMode: "cover" }}
                            />
                          </div>
                          <div className="flight_search_destination">
                            <p>From</p>
                            <h3>
                              {data?.onward_departure_airport?.address?.city}
                            </h3>
                            <h6 style={{ width: 205 }}>
                              {data?.onward_departure_airport?.code}
                              {" - "}
                              {data?.onward_departure_airport?.name}
                            </h6>
                            <h6 style={{ width: 205 }}>
                              {data?.onward_departure_date}
                              {" - "}
                              {data?.onward_departure_time}
                            </h6>
                          </div>
                        </div>
                        <div className="flight_search_middel">
                          <div className="flight_right_arrow">
                            <img
                              src={require("../../assets/img/icon/right_arrow.png")}
                              alt="icon"
                            />
                            <h6>Non-stop</h6>
                          </div>
                          <div className="flight_search_destination">
                            <p>To</p>
                            <h3>
                              {data?.onward_arrival_airport?.address?.city}{" "}
                            </h3>
                            <h6 style={{ width: 205 }}>
                              {data?.onward_arrival_airport?.code}
                              {" - "}
                              {data?.onward_arrival_airport?.name}
                            </h6>
                            <h6 style={{ width: 205 }}>
                              {data?.onward_arrival_date}
                              {" - "}
                              {data?.onward_arrival_time}
                            </h6>
                          </div>
                        </div>
                        <div
                          style={{
                            alignItems: "center",
                          }}
                        >
                          <AirlineSeatReclineNormalSharpIcon
                            fontSize="large"
                            color="info"
                            style={{ alignSelf: "center" }}
                          />
                          <h3
                            style={{
                              fontWeight: "500",
                            }}
                          >
                            {data?.onward_trip_seat_class}
                          </h3>
                        </div>
                      </div>
                    </div>
                  </div>

                  {data?.booking_type === "Round Trip" ? (
                    <div
                      className="flight_search_items tour_booking_form_box"
                      style={{
                        justifyContent: "center",
                        borderWidth: 0,
                        marginTop: 10,
                      }}
                    >
                      <div className="multi_city_flight_lists">
                        <div className="flight_multis_area_wrapper">
                          <div>
                            <div
                              className="booking_success_img"
                              style={{ width: 50, height: 50 }}
                            >
                              {data?.carrier?.logo != null ? (
                                <img
                                  src={getImageUrl(data?.carrier?.logo)}
                                  alt="img"
                                />
                              ) : (
                                <AirlinesRoundedIcon
                                  fontSize="large"
                                  color="info"
                                  style={{ alignSelf: "center" }}
                                />
                              )}
                            </div>
                            <h3 style={{ fontWeight: "500", fontSize: 14 }}>
                              {data?.carrier?.name}
                            </h3>
                            <h3 style={{ fontWeight: "500" }}>
                              {data?.return_trip_flight_number}
                            </h3>
                          </div>
                          <div className="flight_search_left">
                            <div className="flight_logo">
                              <img
                                src={require("../../assets/img/icon/plan-md.png")}
                                width={75}
                                height={75}
                                style={{ resizeMode: "cover" }}
                              />
                            </div>
                            <div className="flight_search_destination">
                              <p>From</p>
                              <h3>
                                {data?.return_departure_airport?.address?.city}
                              </h3>
                              <h6 style={{ width: 205 }}>
                                {data?.return_departure_airport?.code}
                                {" - "}
                                {data?.return_departure_airport?.name}
                              </h6>
                              <h6 style={{ width: 205 }}>
                                {data?.return_departure_date}
                                {" - "}
                                {data?.return_departure_time}
                              </h6>
                            </div>
                          </div>
                          <div className="flight_search_middel">
                            <div className="flight_right_arrow">
                              <img
                                src={require("../../assets/img/icon/right_arrow.png")}
                                alt="icon"
                              />
                              <h6>Non-stop</h6>
                            </div>
                            <div className="flight_search_destination">
                              <p>To</p>
                              <h3>
                                {
                                  data?.return_arrival_airport?.address?.city
                                }{" "}
                              </h3>
                              <h6 style={{ width: 205 }}>
                                {data?.return_arrival_airport?.code}
                                {" - "}
                                {data?.return_arrival_airport?.name}
                              </h6>
                              <h6 style={{ width: 205 }}>
                                {data?.return_arrival_date}
                                {" - "}
                                {data?.return_arrival_time}
                              </h6>
                            </div>
                          </div>
                          <div
                            style={{
                              alignItems: "center",
                            }}
                          >
                            <AirlineSeatReclineNormalSharpIcon
                              fontSize="large"
                              color="info"
                              style={{ alignSelf: "center" }}
                            />
                            <h3
                              style={{
                                fontWeight: "500",
                              }}
                            >
                              {data?.return_trip_seat_class}
                            </h3>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : null}

                  <h3
                    className="heading_theme"
                    style={{ paddingBottom: 0, marginBottom: 0, marginTop: 40 }}
                  >
                    Passenger Information
                  </h3>

                  {data?.passengers?.map((passenger) => {
                    return (
                      <div
                        className="tour_booking_form_box"
                        style={{ marginTop: 20 }}
                      >
                        <div className="your_info_arae">
                          <ul>
                            <li
                              style={{
                                display: "flex",
                                justifyContent: "flex-end",
                              }}
                            >
                              <Link
                                to={pnrURL(
                                  data?.reference_number,
                                  passenger?.pnr,
                                )}
                                target="_blank"
                              >
                                <LocalPrintshopRoundedIcon
                                  className="last_name"
                                  fontSize="large"
                                  color="inherit"
                                  style={{ alignSelf: "flex-end" }}
                                />
                              </Link>
                            </li>

                            <li>
                              <span className="name_first">Title:</span>
                              <span className="last_name">
                                {passenger?.title ?? "-"}
                              </span>
                            </li>

                            <li>
                              <span className="name_first">First Name:</span>
                              <span className="last_name">
                                {passenger?.first_name ?? "-"}
                              </span>
                            </li>

                            <li>
                              <span className="name_first">Last Name:</span>
                              <span className="last_name">
                                {passenger?.last_name ?? "-"}
                              </span>
                            </li>

                            <li>
                              <span className="name_first">PNR:</span>
                              <span className="last_name">
                                {passenger?.pnr ?? "-"}
                              </span>
                            </li>

                            <li>
                              <span className="name_first">PAX TYPE:</span>
                              <span className="last_name">
                                {passenger?.pax_type ?? "-"}
                              </span>
                            </li>

                            <li>
                              <span className="name_first">Gender:</span>
                              <span className="last_name">
                                {passenger?.gender ?? "-"}
                              </span>
                            </li>

                            <li>
                              <span className="name_first">Nationality:</span>
                              <span className="last_name">
                                {passenger?.nationality ?? "-"}
                              </span>
                            </li>

                            <li>
                              <span className="name_first">Email address:</span>{" "}
                              <span className="last_name">
                                {passenger?.email ?? "-"}
                              </span>
                            </li>
                            <li>
                              <span className="name_first">DOB:</span>{" "}
                              <span className="last_name">
                                {passenger?.dob ?? "-"}
                              </span>
                            </li>

                            <li>
                              <span className="name_first">
                                PASSPORT NUMBER:
                              </span>
                              <span className="last_name">
                                {passenger?.passport_number ?? "-"}
                              </span>
                            </li>

                            <li>
                              <span className="name_first">
                                PASSPORT ISSUE DATE:
                              </span>
                              <span className="last_name">
                                {passenger?.passport_issued_at ?? "-"}
                              </span>
                            </li>
                            <li>
                              <span className="name_first">
                                PASSPORT EXPIRY DATE:
                              </span>
                              <span className="last_name">
                                {passenger?.passport_expiring_at ?? "-"}
                              </span>
                            </li>
                            <li>
                              <span className="name_first">
                                PASSPORT ISSUE COUNTRY:
                              </span>{" "}
                              <span className="last_name">
                                {passenger?.passport_issue_country ?? "-"}
                              </span>
                            </li>
                            <li>
                              <span className="name_first">CONTACT NO:</span>{" "}
                              <span className="last_name">
                                {passenger?.contact_number ?? "-"}
                              </span>
                            </li>

                            <li>
                              <span className="name_first">WHEEL CHAIR:</span>{" "}
                              <span className="last_name">
                                {passenger?.wheel_chair_required === true
                                  ? "Yes"
                                  : "No"}
                              </span>
                            </li>

                            <li>
                              <span className="name_first">VEG MEAL:</span>{" "}
                              <span className="last_name">
                                {passenger?.veg_meal === true ? "Yes" : "No"}
                              </span>
                            </li>
                          </ul>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="col-lg-4">
              <div className="tour_details_right_sidebar_wrapper">
                <div className="tour_detail_right_sidebar">
                  <div className="tour_details_right_boxed">
                    <div className="tour_details_right_box_heading">
                      <h3>Booking details</h3>
                    </div>
                    <div className="tour_booking_amount_area">
                      <ul>
                        <li>
                          Booking ID: <span>{data?.reference_number}</span>
                        </li>
                        <li>
                          Booking date:{" "}
                          <span>{data?.onward_departure_date}</span>
                        </li>
                        <li>
                          Booking status:{" "}
                          <span
                            style={{
                              color: data?.status === DELETED ? RED : null,
                            }}
                          >
                            {data?.status === DELETED
                              ? CANCELLED_BY_ADMIN
                              : data?.status}
                          </span>
                        </li>
                      </ul>
                      {data?.booking_type === "One Way" ? (
                        <>
                          <ul>
                            <li>
                              Adult x {countPassengerOnType("Adult")}
                              <span>
                                €
                                {countPassengerOnType("Adult") *
                                  (priceRound(
                                    parseInt(data?.onward_trip_adult_price),
                                  ) || 0.0)}
                              </span>
                            </li>
                            {countPassengerOnType("Child") !== 0 ? (
                              <li>
                                Child x {countPassengerOnType("Child")}
                                <span>
                                  €
                                  {countPassengerOnType("Child") *
                                    (priceRound(
                                      parseInt(data?.onward_trip_child_price),
                                    ) || 0.0)}
                                </span>
                              </li>
                            ) : null}
                            {countPassengerOnType("Infant") !== 0 ? (
                              <li>
                                Infant x {countPassengerOnType("Infant")}
                                <span>
                                  €
                                  {countPassengerOnType("Infant") *
                                    (priceRound(
                                      parseInt(data?.onward_trip_infant_price),
                                    ) || 0.0)}
                                </span>
                              </li>
                            ) : null}
                          </ul>
                          <div className="total_subtotal_booking">
                            <h6 className="remove_coupon_tour">
                              Subtotal{" "}
                              <span>€{priceRound(data.sub_total)}</span>
                            </h6>
                          </div>
                          <div className="total_subtotal_booking">
                            <h6 className="remove_coupon_tour">
                              Total Amount{" "}
                              <span>€{priceRound(data.total)}</span>
                            </h6>
                          </div>
                        </>
                      ) : (
                        <>
                          <ul>
                            <li>
                              Adult x {countPassengerOnType("Adult")}
                              <span>
                                €
                                {countPassengerOnType("Adult") *
                                  (priceRound(
                                    parseInt(data?.onward_trip_adult_price),
                                  ) || 0.0) +
                                  countPassengerOnType("Adult") *
                                    (priceRound(
                                      parseInt(data?.return_trip_adult_price),
                                    ) || 0.0)}
                              </span>
                            </li>
                            {countPassengerOnType("Child") != 0 ? (
                              <li>
                                Child x {countPassengerOnType("Child")}
                                <span>
                                  €
                                  {countPassengerOnType("Child") *
                                    (priceRound(
                                      parseInt(data?.onward_trip_child_price),
                                    ) || 0.0) +
                                    countPassengerOnType("Child") *
                                      (priceRound(
                                        parseInt(data?.return_trip_child_price),
                                      ) || 0.0)}
                                </span>
                              </li>
                            ) : null}
                            {countPassengerOnType("Infant") != 0 ? (
                              <li>
                                Infant x {countPassengerOnType("Infant")}
                                <span>
                                  €
                                  {countPassengerOnType("Infant") *
                                    (priceRound(
                                      parseInt(data?.onward_trip_infant_price),
                                    ) || 0.0) +
                                    countPassengerOnType("Infant") *
                                      (priceRound(
                                        parseInt(
                                          data?.return_trip_infant_price,
                                        ),
                                      ) || 0.0)}
                                </span>
                              </li>
                            ) : null}
                          </ul>
                          <div className="total_subtotal_booking">
                            <h6 className="remove_coupon_tour">
                              Subtotal{" "}
                              <span>€{priceRound(data.sub_total)}</span>
                            </h6>
                          </div>
                          {data?.discount !== null &&
                          parseFloat(data?.discount) !== 0.0 ? (
                            <div className="total_subtotal_booking">
                              <h6 className="remove_coupon_tour">
                                Discount{" "}
                                <span>- €{priceRound(data.discount)}</span>
                              </h6>
                            </div>
                          ) : null}
                          <div className="total_subtotal_booking">
                            <h6 className="remove_coupon_tour">
                              Total Amount{" "}
                              <span>€{priceRound(data.total)}</span>
                            </h6>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="tour_details_right_sidebar_wrapper">
                <div
                  className="tour_details_right_boxed"
                  style={{
                    height: 50,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <Link to={invoiceURL(data?.reference_number)} target="_blank">
                    <h3
                      style={{
                        textAlign: "center",
                        fontWeight: "500",
                        fontSize: 20,
                        marginTop: 10,
                      }}
                    >
                      View Invoice
                    </h3>
                  </Link>
                </div>
              </div>
              <div
                className="tour_details_right_sidebar_wrapper"
                style={{ marginTop: 20 }}
              >
                <div
                  className="tour_details_right_boxed"
                  style={{
                    height: 50,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <Link
                    to={pnrURL(data?.reference_number, "all")}
                    target="_blank"
                  >
                    <h3
                      style={{
                        textAlign: "center",
                        fontWeight: "500",
                        fontSize: 20,
                        marginTop: 10,
                      }}
                    >
                      Download All PNR
                    </h3>
                  </Link>
                </div>
              </div>
            </div>
          </div>

          <Dialog open={isEditOpen} onClose={closeEdit} fullWidth maxWidth="lg">
            <DialogTitle>Edit Booking</DialogTitle>
            <DialogContent dividers>
              <div className="row" style={{ marginTop: 10 }}>
                {editPassengers.map((passenger, index) => (
                  <div
                    className="col-lg-12"
                    key={index}
                    style={{ marginBottom: 24 }}
                  >
                    <div className="tour_booking_form_box">
                      <h3 style={{ fontSize: 18, marginBottom: 16 }}>
                        Passenger {index + 1} - {passenger?.pax_type || "-"}
                      </h3>
                      <div className="row">
                        <div className="col-lg-4 col-md-6">
                          <select
                            className="form-control form-select bg_input"
                            value={passenger.title}
                            onChange={(e) =>
                              updatePassengerField(
                                index,
                                "title",
                                e.target.value,
                              )
                            }
                            style={{
                              borderColor: passengerFieldError(
                                passenger,
                                "title",
                              )
                                ? "#d32f2f"
                                : null,
                            }}
                          >
                            <option value="">Title*</option>
                            <option value="MR">MR</option>
                            <option value="MRS">MRS</option>
                          </select>
                          {passengerFieldError(passenger, "title") ? (
                            <div style={{ color: "#d32f2f", fontSize: 12 }}>
                              Please select title
                            </div>
                          ) : null}
                        </div>
                        <div className="col-lg-4 col-md-6">
                          <TextField
                            fullWidth
                            label="First Name"
                            value={passenger.first_name}
                            onChange={(e) =>
                              updatePassengerField(
                                index,
                                "first_name",
                                e.target.value,
                              )
                            }
                            margin="dense"
                            error={passengerFieldError(passenger, "first_name")}
                          />
                          {passengerFieldError(passenger, "first_name") ? (
                            <div style={{ color: "#d32f2f", fontSize: 12 }}>
                              First name cannot be blank
                            </div>
                          ) : null}
                        </div>
                        <div className="col-lg-4 col-md-6">
                          <TextField
                            fullWidth
                            label="Last Name"
                            value={passenger.last_name}
                            onChange={(e) =>
                              updatePassengerField(
                                index,
                                "last_name",
                                e.target.value,
                              )
                            }
                            margin="dense"
                            error={passengerFieldError(passenger, "last_name")}
                          />
                          {passengerFieldError(passenger, "last_name") ? (
                            <div style={{ color: "#d32f2f", fontSize: 12 }}>
                              Last name cannot be blank
                            </div>
                          ) : null}
                        </div>
                        <div className="col-lg-4 col-md-6">
                          <TextField
                            fullWidth
                            label="Contact Number"
                            value={passenger.contact_number}
                            onChange={(e) =>
                              updatePassengerField(
                                index,
                                "contact_number",
                                e.target.value,
                              )
                            }
                            margin="dense"
                            error={passengerFieldError(
                              passenger,
                              "contact_number",
                            )}
                          />
                          {passengerFieldError(passenger, "contact_number") ? (
                            <div style={{ color: "#d32f2f", fontSize: 12 }}>
                              Enter a valid contact number
                            </div>
                          ) : null}
                        </div>
                        <div className="col-lg-4 col-md-6">
                          <TextField
                            fullWidth
                            label="Email"
                            value={passenger.email}
                            onChange={(e) =>
                              updatePassengerField(
                                index,
                                "email",
                                e.target.value,
                              )
                            }
                            margin="dense"
                            error={passengerFieldError(passenger, "email")}
                          />
                          {passengerFieldError(passenger, "email") ? (
                            <div style={{ color: "#d32f2f", fontSize: 12 }}>
                              Enter a valid email address
                            </div>
                          ) : null}
                        </div>
                        <div className="col-lg-4 col-md-6">
                          <TextField
                            fullWidth
                            label="Passport Number"
                            value={passenger.passport_number}
                            onChange={(e) =>
                              updatePassengerField(
                                index,
                                "passport_number",
                                e.target.value,
                              )
                            }
                            margin="dense"
                            error={passengerFieldError(
                              passenger,
                              "passport_number",
                            )}
                          />
                          {passengerFieldError(passenger, "passport_number") ? (
                            <div style={{ color: "#d32f2f", fontSize: 12 }}>
                              Passport number must be 6-15 alphanumeric
                              characters
                            </div>
                          ) : null}
                        </div>
                        <div className="col-lg-4 col-md-6">
                          <TextField
                            fullWidth
                            type="date"
                            label="DOB"
                            value={passenger.dob}
                            onChange={(e) =>
                              updatePassengerField(index, "dob", e.target.value)
                            }
                            margin="dense"
                            InputLabelProps={{ shrink: true }}
                            error={passengerFieldError(passenger, "dob")}
                          />
                          {passengerFieldError(passenger, "dob") ? (
                            <div style={{ color: "#d32f2f", fontSize: 12 }}>
                              {passenger?.pax_type === "Adult"
                                ? "Adult passengers must be at least 12 years old"
                                : "Please select date of birth"}
                            </div>
                          ) : null}
                        </div>
                        <div className="col-lg-4 col-md-6">
                          <TextField
                            fullWidth
                            type="date"
                            label="Passport Issue"
                            value={passenger.passport_issued_at}
                            onChange={(e) =>
                              updatePassengerField(
                                index,
                                "passport_issued_at",
                                e.target.value,
                              )
                            }
                            margin="dense"
                            InputLabelProps={{ shrink: true }}
                            error={passengerFieldError(
                              passenger,
                              "passport_issued_at",
                            )}
                          />
                          {passengerFieldError(
                            passenger,
                            "passport_issued_at",
                          ) ? (
                            <div style={{ color: "#d32f2f", fontSize: 12 }}>
                              Passport issue must be after date of birth
                            </div>
                          ) : null}
                        </div>
                        <div className="col-lg-4 col-md-6">
                          <TextField
                            fullWidth
                            type="date"
                            label="Passport Expiry"
                            value={passenger.passport_expiring_at}
                            onChange={(e) =>
                              updatePassengerField(
                                index,
                                "passport_expiring_at",
                                e.target.value,
                              )
                            }
                            margin="dense"
                            InputLabelProps={{ shrink: true }}
                            error={passengerFieldError(
                              passenger,
                              "passport_expiring_at",
                            )}
                          />
                          {passengerFieldError(
                            passenger,
                            "passport_expiring_at",
                          ) ? (
                            <div style={{ color: "#d32f2f", fontSize: 12 }}>
                              Passport expiry must be after passport issue date
                            </div>
                          ) : null}
                        </div>
                        <div className="col-lg-4 col-md-6">
                          <select
                            className="form-control form-select bg_input"
                            value={passenger.passport_issue_country}
                            onChange={(e) =>
                              updatePassengerField(
                                index,
                                "passport_issue_country",
                                e.target.value,
                              )
                            }
                            style={{
                              borderColor: passengerFieldError(
                                passenger,
                                "passport_issue_country",
                              )
                                ? "#d32f2f"
                                : null,
                            }}
                          >
                            <option value="">Issue Country*</option>
                            {CountriesList.map((country) => (
                              <option key={country} value={country}>
                                {country}
                              </option>
                            ))}
                          </select>
                          {passengerFieldError(
                            passenger,
                            "passport_issue_country",
                          ) ? (
                            <div style={{ color: "#d32f2f", fontSize: 12 }}>
                              Please enter issue country
                            </div>
                          ) : null}
                        </div>
                        <div className="col-lg-4 col-md-6">
                          <select
                            className="form-control form-select bg_input"
                            value={passenger.nationality}
                            onChange={(e) =>
                              updatePassengerField(
                                index,
                                "nationality",
                                e.target.value,
                              )
                            }
                            style={{
                              borderColor: passengerFieldError(
                                passenger,
                                "nationality",
                              )
                                ? "#d32f2f"
                                : null,
                            }}
                          >
                            <option value="">Nationality*</option>
                            {CountriesList.map((country) => (
                              <option key={country} value={country}>
                                {country}
                              </option>
                            ))}
                          </select>
                          {passengerFieldError(passenger, "nationality") ? (
                            <div style={{ color: "#d32f2f", fontSize: 12 }}>
                              Please enter nationality
                            </div>
                          ) : null}
                        </div>
                        <div className="col-lg-4 col-md-6">
                          <select
                            className="form-control form-select bg_input"
                            value={passenger.gender}
                            onChange={(e) =>
                              updatePassengerField(
                                index,
                                "gender",
                                e.target.value,
                              )
                            }
                            style={{
                              borderColor: passengerFieldError(
                                passenger,
                                "gender",
                              )
                                ? "#d32f2f"
                                : null,
                            }}
                          >
                            <option value="">Gender*</option>
                            <option value="Male">Male</option>
                            <option value="Female">Female</option>
                          </select>
                          {passengerFieldError(passenger, "gender") ? (
                            <div style={{ color: "#d32f2f", fontSize: 12 }}>
                              Please enter gender
                            </div>
                          ) : null}
                        </div>
                        <div className="col-lg-12" style={{ marginTop: 12 }}>
                          <label style={{ marginRight: 20 }}>
                            <input
                              type="checkbox"
                              checked={!!passenger.wheel_chair_required}
                              onChange={(e) =>
                                updatePassengerField(
                                  index,
                                  "wheel_chair_required",
                                  e.target.checked,
                                )
                              }
                            />{" "}
                            Wheel chair
                          </label>
                          <label>
                            <input
                              type="checkbox"
                              checked={!!passenger.veg_meal}
                              onChange={(e) =>
                                updatePassengerField(
                                  index,
                                  "veg_meal",
                                  e.target.checked,
                                )
                              }
                            />{" "}
                            Veg meal
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div
                style={{ display: "flex", justifyContent: "flex-end", gap: 12 }}
              >
                <Button
                  variant="outlined"
                  onClick={closeEdit}
                  style={{
                    borderColor: "#c8a24a",
                    color: "#2b2540",
                    fontWeight: 600,
                  }}
                >
                  Close
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSaveBooking}
                  style={{
                    background:
                      "linear-gradient(180deg, #3a3a3a 0%, #222222 100%)",
                    color: "#fff",
                    fontWeight: 600,
                  }}
                >
                  Save Changes
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          <Dialog
            open={showCancelConfirm}
            onClose={() => setShowCancelConfirm(false)}
          >
            <DialogTitle>Cancel booking?</DialogTitle>
            <DialogContent dividers>
              Are you sure you want to cancel this booking? This action cannot
              be undone.
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 12,
                  marginTop: 20,
                }}
              >
                <Button
                  variant="outlined"
                  onClick={() => setShowCancelConfirm(false)}
                  style={{ borderColor: "#c8a24a", color: "#2b2540" }}
                >
                  Keep Booking
                </Button>
                <Button
                  variant="contained"
                  onClick={async () => {
                    setShowCancelConfirm(false);
                    await handleCancelBooking();
                  }}
                  style={{
                    background:
                      "linear-gradient(180deg, #3a3a3a 0%, #222222 100%)",
                    color: "#fff",
                  }}
                >
                  Confirm Cancel
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default BookingDetails;
